import { Eyebrow } from "./Eyebrow";
import { cn } from "@/lib/cn";

type Wash = "plain" | "surface" | "wash";

/** A full-width section band with consistent vertical rhythm + max-width container. */
export function Section({
  wash = "plain",
  className,
  containerClassName,
  bordered = false,
  children,
}: {
  wash?: Wash;
  className?: string;
  containerClassName?: string;
  bordered?: boolean;
  children: React.ReactNode;
}) {
  const bg = wash === "surface" ? "bg-surface" : wash === "wash" ? "bg-wash" : "";
  return (
    <section className={cn(bg, bordered && "border-y border-border", className)}>
      <div className={cn("mx-auto max-w-6xl px-6 py-20", containerClassName)}>{children}</div>
    </section>
  );
}

/** Eyebrow + title + optional intro. Centered by default. */
export function SectionHeading({
  eyebrow,
  title,
  intro,
  align = "center",
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  align?: "center" | "left";
  className?: string;
}) {
  const center = align === "center";
  return (
    <div className={cn(center ? "mx-auto max-w-3xl text-center" : "max-w-2xl", className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className={cn("text-3xl font-extrabold tracking-tight sm:text-4xl", eyebrow && "mt-4")}>
        {title}
      </h2>
      {intro && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{intro}</p>}
    </div>
  );
}

/** A headline number + label, for stat strips. */
export function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-4xl font-extrabold tracking-tight text-primary">{value}</div>
      <div className="mt-1 text-sm font-medium text-muted-foreground">{label}</div>
    </div>
  );
}

/** A terminal/notebook-style code card with a title bar. */
export function CodeCard({
  title,
  lang = "shell",
  code,
  className,
}: {
  title?: string;
  lang?: string;
  code: string;
  className?: string;
}) {
  return (
    <div className={cn("overflow-hidden rounded-2xl border border-border bg-[hsl(222_28%_12%)] shadow-lift", className)}>
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-signal/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-accent" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary" />
        <span className="ml-2 font-mono text-xs text-white/50">{title ?? lang}</span>
      </div>
      <pre className="overflow-x-auto px-4 py-4 text-[13px] leading-relaxed">
        <code className="font-mono text-[hsl(213_38%_88%)]">{code}</code>
      </pre>
    </div>
  );
}

/** A small rounded tag/pill. */
export function Pill({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "primary" | "accent" }) {
  const tones = {
    neutral: "border border-border bg-card text-muted-foreground",
    primary: "bg-primary-tint text-primary",
    accent: "bg-accent-tint text-foreground",
  };
  return (
    <span className={cn("inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold", tones[tone])}>
      {children}
    </span>
  );
}
